import React, { useState, useEffect } from 'react'
import { Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react'

export default function ContactForm({ defaultService = '', title = 'Kontakt os i dag', subtitle = '' }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: defaultService,
    message: '',
    website_url: '' // Honeypot
  })

  const [status, setStatus] = useState('idle') // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    if (defaultService) {
      setFormData(prev => ({ ...prev, service: defaultService }))
    }
  }, [defaultService])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setStatus('submitting')
    setErrorMessage('')

    try {
      const response = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        setStatus('success')
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          message: '',
          website_url: ''
        })
      } else {
        const errData = await response.json().catch(() => ({}))
        throw new Error(errData.message || 'Der skete en fejl under afsendelsen. Prøv venligst igen.')
      }
    } catch (error) {
      console.error('Form submission error:', error)
      setStatus('error')
      setErrorMessage(error.message || 'Der skete en fejl under afsendelsen. Prøv venligst igen.')
    }
  }

  return (
    <div className="bg-white rounded-none border border-stone-200 p-6 sm:p-8 shadow-sm hover:shadow-md transition-shadow duration-300">
      <div className="mb-6">
        <h3 className="text-2xl font-bold text-stone-900 tracking-tight">
          {title}
        </h3>
        {subtitle ? (
          <p className="text-sm text-stone-600 mt-1.5">
            {subtitle}
          </p>
        ) : null}
      </div>

      {status === 'success' ? (
        <div className="rounded-none bg-orange-50 border border-orange-200 p-6 text-center">
          <CheckCircle className="h-12 w-12 text-orange-600 mx-auto mb-3" />
          <h4 className="font-semibold text-orange-600 text-lg">Tak for din henvendelse!</h4>
          <p className="text-sm text-orange-600 mt-2">
            Vi har modtaget din besked og vil kontakte dig hurtigst muligt, typisk inden for et par timer.
          </p>
          <button
            onClick={() => setStatus('idle')}
            className="mt-5 text-sm font-semibold text-orange-600 hover:underline"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Honeypot field - absolutely hidden from humans */}
          <div className="sr-only absolute" aria-hidden="true">
            <label htmlFor="website_url">Venligst lad dette felt være tomt</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="name" className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
                Dit navn *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full rounded-none border border-stone-300 bg-stone-50 px-3.5 py-2.5 text-sm text-stone-900 focus:border-orange-600 focus:bg-white focus:outline-none focus:ring-1 focus:ring-orange-600 transition-colors"
                placeholder="Peter Jensen"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
                Telefonnummer *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                className="w-full rounded-none border border-stone-300 bg-stone-50 px-3.5 py-2.5 text-sm text-stone-900 focus:border-orange-600 focus:bg-white focus:outline-none focus:ring-1 focus:ring-orange-600 transition-colors"
                placeholder="12 34 56 78"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="email" className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
                E-mailadresse *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full rounded-none border border-stone-300 bg-stone-50 px-3.5 py-2.5 text-sm text-stone-900 focus:border-orange-600 focus:bg-white focus:outline-none focus:ring-1 focus:ring-orange-600 transition-colors"
                placeholder="navn@firma.dk"
              />
            </div>

            <div>
              <label htmlFor="company" className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
                Virksomhed (valgfri)
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="w-full rounded-none border border-stone-300 bg-stone-50 px-3.5 py-2.5 text-sm text-stone-900 focus:border-orange-600 focus:bg-white focus:outline-none focus:ring-1 focus:ring-orange-600 transition-colors"
                placeholder="Firma ApS"
              />
            </div>
          </div>

          <div>
            <label htmlFor="service" className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
              Hvad kan vi hjælpe med?
            </label>
            <select
              id="service"
              name="service"
              value={formData.service}
              onChange={handleChange}
              className="w-full rounded-none border border-stone-300 bg-stone-50 px-3.5 py-2.5 text-sm text-stone-900 focus:border-orange-600 focus:bg-white focus:outline-none focus:ring-1 focus:ring-orange-600 transition-colors"
            >
              <option value="">Vælg en ydelse...</option>
              <option value="regnskabsservice">Regnskabsservice (Samlet pakke)</option>
              <option value="bogfoering">Bogføring & moms</option>
              <option value="aarsregnskab">Årsrapport & årsregnskab</option>
              <option value="aarsregnskab-og-bogfoering">Årsregnskab og bogføring</option>
              <option value="personlig-selvangivelse">Personlig selvangivelse</option>
              <option value="andet">Andet / Uforpligtende samtale</option>
            </select>
          </div>

          <div>
            <label htmlFor="message" className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
              Beskriv din opgave eller situation *
            </label>
            <textarea
              id="message"
              name="message"
              required
              rows="4"
              value={formData.message}
              onChange={handleChange}
              className="w-full rounded-none border border-stone-300 bg-stone-50 px-3.5 py-2.5 text-sm text-stone-900 focus:border-orange-600 focus:bg-white focus:outline-none focus:ring-1 focus:ring-orange-600 transition-colors resize-none"
              placeholder="Hej, jeg har brug for hjælp til..."
            />
          </div>

          {status === 'error' && (
            <div className="rounded-none bg-rose-50 border border-rose-200 p-3.5 flex items-start gap-2.5 text-rose-800 text-xs">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{errorMessage}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={status === 'submitting'}
            className="w-full inline-flex items-center justify-center gap-2 rounded-none bg-orange-600 px-5 py-3.5 text-sm font-semibold text-white shadow-sm hover:bg-orange-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600 disabled:opacity-70 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg cursor-pointer"
          >
            {status === 'submitting' ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Sender besked...
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Kontakt os i dag
              </>
            )}
          </button>

          <p className="text-[11px] text-stone-500 text-center mt-2">
            * Ved at indsende accepterer du, at vi må kontakte dig angående din henvendelse.
          </p>
        </form>
      )}
    </div>
  )
}
